/** Towns: the build graph, income, weekly growth, the mage guild, recruitment.
 *
 * Every number here is data. Income is a set of `income.*` modifiers on the
 * built buildings (the hall chain uses `max`, so City Hall simply outbids Town
 * Hall instead of stacking with it); growth is a `growth` multiplier plus
 * `growth.tierN` adds. The kernel resolves them; it never names a building.
 */

import type {
  ArmyId, BuildingDefId, CreatureDefId, HeroId, PlayerId, SpellDefId, TownId,
} from "../ids.js";
import type { BuildingDef, ContentRegistry, SpellSchool } from "../content/registry.js";
import type { Explained, Modifier, StatKey } from "../modifiers.js";
import { resolveStat } from "../modifiers.js";
import { FP_ONE } from "../numeric.js";
import type { Reason } from "../commands.js";
import type { HeroState, ResourceKind, Resources, TownState } from "../state.js";
import { entityKeys } from "../util/iter.js";
import type { Ctx } from "./ctx.js";
import { patchPlayer, patchTown, rollBounded, rollWeighted } from "./ctx.js";
import { asHeroDefId } from "./ids.js";
import { heroModifiers, learnIllegality, learnSpell } from "./hero.js";
import { applyTownVisit, builtDefs } from "./buildings.js";
import { GRAIL_SLOT, grailBearerIn } from "./grail.js";
import type { UpgradeOffer } from "./upgrade.js";
import { FULL_PRICE_BP, upgradeOffers } from "./upgrade.js";

/** Spells offered by each mage-guild level, the classic's 5/4/3/2/1. */
export const GUILD_SPELL_COUNT: readonly number[] = Object.freeze([5, 4, 3, 2, 1]);

/** Only one Capitol per player; capturing a second downgrades it (Section 12). */
export const CAPITOL_SLOT = "hall";
export const CAPITOL_INCOME = 4000;

export function buildingsOfFaction(content: ContentRegistry, faction: string): BuildingDef[] {
  const out: BuildingDef[] = [];
  for (const id of [...content.buildings.keys()].sort()) {
    const b = content.buildings.get(id)!;
    if ((b.faction as string) === faction) out.push(b);
  }
  return out;
}

export { builtDefs } from "./buildings.js";

export function townModifiers(c: Ctx, t: TownState): readonly Modifier[] {
  const out: Modifier[] = [];
  for (const b of builtDefs(c, t)) for (const m of b.modifiers) out.push(m);
  for (const m of t.modifiers) out.push(m);
  return out;
}

// ----------------------------------------------------------------- income

export const INCOME_STAT: Readonly<Record<ResourceKind, StatKey>> = Object.freeze({
  gold: "income.gold", wood: "income.wood", ore: "income.ore",
  mercury: "income.mercury", sulfur: "income.sulfur",
  crystal: "income.crystal", gems: "income.gems",
});

export function explainTownIncome(c: Ctx, t: TownState, r: ResourceKind): Explained<number> {
  const stat = INCOME_STAT[r];
  return resolveStat(0, townModifiers(c, t).filter((m) => m.stat === stat));
}

export const townIncome = (c: Ctx, t: TownState, r: ResourceKind): number => {
  const v = explainTownIncome(c, t, r).value;
  return v < 0 ? 0 : v;
};

// ----------------------------------------------------------------- growth

/**
 * Weekly growth of one creature in one town.
 *
 * The fortification multiplier is the one stat in the game rounded *down*
 * rather than half away from zero (Section 12: "Citadel: generally x1.5
 * creature growth, rounded down"), so it is resolved separately from the
 * additive terms and floored. Everything else goes through `resolveStat`.
 */
export function growthFor(c: Ctx, t: TownState, creature: CreatureDefId): number {
  const def = c.content.creatures.get(creature);
  if (def === undefined) return 0;
  const mods = [...townModifiers(c, t), ...heroGrowthModifiers(c, t)];
  const tierStat = `growth.tier${def.tier}` as StatKey;
  const adds = mods.filter((m) =>
    (m.stat === "growth" || m.stat === tierStat) && m.op !== "mulFp");
  const muls = mods.filter((m) => m.stat === "growth" && m.op === "mulFp");
  const base = resolveStat(def.growth, adds).value;
  if (muls.length === 0) return base < 0 ? 0 : base;
  const factor = resolveStat(FP_ONE, muls).value;
  const v = Math.floor((base * factor) / FP_ONE);
  return v < 0 ? 0 : v;
}

/**
 * What the owner's heroes wear that grows creatures here. The town's ledger
 * read its buildings alone for a long time, so the six Legion artifacts were
 * authored, described, equipped and never counted. Two rules, both the classic's
 * (the Idol of the Host page on the genre's reference tables):
 *
 *  - A *part* of the Legion - a `growth.tierN` add - counts only in the town
 *    its bearer is stationed in, garrison or visiting, at the moment growth
 *    is dealt. Two bearers in one town both count; a second identical part on
 *    one hero does not.
 *  - The assembled Statue's `growth` multiplier counts in every town the
 *    owner has wherever the bearer stands, and once: a second Statue anywhere
 *    in the kingdom adds nothing.
 *
 * Read off `heroModifiers`, so skills, specialties and map effects that ever
 * carry a growth stat arrive by the same door.
 */
export function heroGrowthModifiers(c: Ctx, t: TownState): readonly Modifier[] {
  const out: Modifier[] = [];
  const kingdomSeen = new Set<string>();
  for (const id of entityKeys(c.s.heroes)) {
    const h = c.s.heroes[id]!;
    if (h.owner !== t.owner) continue;
    const here = t.visitingHero === h.id || t.garrisonHero === h.id;
    const seen = new Set<string>();
    for (const m of heroModifiers(c, h)) {
      const stat = m.stat as string;
      if (stat !== "growth" && !stat.startsWith("growth.")) continue;
      const key = `${m.source as string} ${stat} ${m.op}`;
      if (stat === "growth" && m.op === "mulFp") {
        if (kingdomSeen.has(key)) continue;
        kingdomSeen.add(key);
        out.push(m);
        continue;
      }
      if (!here || seen.has(key)) continue;
      seen.add(key);
      out.push(m);
    }
  }
  return out;
}

/** The creatures this town's built dwellings produce, in tier order. */
export function dwellingCreatures(c: Ctx, t: TownState): readonly CreatureDefId[] {
  const out: CreatureDefId[] = [];
  const seen = new Set<string>();
  for (const b of builtDefs(c, t)) {
    if (b.dwellingFor === null) continue;
    if (seen.has(b.dwellingFor as string)) continue;
    seen.add(b.dwellingFor as string);
    out.push(b.dwellingFor);
  }
  out.sort((a, b) => {
    const da = c.content.creatures.get(a), db = c.content.creatures.get(b);
    const ta = da?.tier ?? 0, tb = db?.tier ?? 0;
    return ta - tb || (a < b ? -1 : a > b ? 1 : 0);
  });
  return out;
}

/**
 * Add one week of creatures to a town's dwellings.
 *
 * A dwelling and its upgrade share one pool in the classic: building the upgrade does
 * not double production, and creatures banked before the upgrade may still be
 * bought as the base creature. Modelled by keeping the pool under the *built*
 * dwelling's creature and letting the recruit command spend it on either.
 */
export interface GrowthEvent {
  /** Multiplier on every creature in the town, fixed point. */
  readonly allMulFp: number;
  /** The creature a "Week of the ..." names, if any. */
  readonly creature: CreatureDefId | null;
  readonly creatureMulFp: number;
  readonly creatureFlat: number;
}

export const PLAIN_WEEK: GrowthEvent = Object.freeze({
  allMulFp: FP_ONE, creature: null, creatureMulFp: FP_ONE, creatureFlat: 0,
});

export function applyWeeklyGrowth(c: Ctx, id: TownId, ev: GrowthEvent): void {
  const t = c.s.towns[id];
  if (t === undefined) return;
  const available: Record<string, number> = { ...t.available };
  for (const creature of dwellingCreatures(c, t)) {
    let n = growthFor(c, t, creature);
    if (ev.allMulFp !== FP_ONE) n = Math.floor((n * ev.allMulFp) / FP_ONE);
    if (ev.creature !== null && ev.creature === creature) {
      if (ev.creatureMulFp !== FP_ONE) n = Math.floor((n * ev.creatureMulFp) / FP_ONE);
      n += ev.creatureFlat;
    }
    if (n <= 0) continue;
    available[creature as string] = (available[creature as string] ?? 0) + n;
    c.events.push({ type: "CreaturesGrew", town: id, creature, count: n });
  }
  patchTown(c, id, { available });
}

/** A plague month halves every town's stock, rounded down (Section 12). */
export function halveAvailable(c: Ctx, id: TownId): void {
  const t = c.s.towns[id];
  if (t === undefined) return;
  const available: Record<string, number> = {};
  for (const k of Object.keys(t.available).sort()) {
    available[k] = Math.floor((t.available[k] ?? 0) / 2);
  }
  patchTown(c, id, { available });
}

// --------------------------------------------------------------- buildings

export function guildLevelOf(c: Ctx, t: TownState): number {
  let lvl = 0;
  for (const b of builtDefs(c, t)) if (b.guildLevel > lvl) lvl = b.guildLevel;
  return lvl;
}

/** Is `b` legal to build in `t` right now? Returns the reason it is not. */
export function buildIllegality(c: Ctx, t: TownState, id: BuildingDefId): Reason | null {
  const b = c.content.buildings.get(id);
  if (b === undefined) return { code: "NO_SUCH_ENTITY", id: id as string };
  if ((b.faction as string) !== (t.faction as string)) return { code: "WRONG_TERRAIN" };
  if (t.built.includes(id)) return { code: "ALREADY_BUILT", building: id };
  if (t.builtThisTurn) return { code: "BUILD_LIMIT_REACHED" };
  for (const req of b.requires) {
    if (!t.built.includes(req)) return { code: "MISSING_PREREQUISITE", building: req };
  }
  if (b.upgradeOf !== null && !t.built.includes(b.upgradeOf)) {
    return { code: "MISSING_PREREQUISITE", building: b.upgradeOf };
  }
  // Section 39: the one building whose prerequisite is not a building. Somebody
  // in the town has to be holding the Grail, and raising it spends the relic.
  if (b.slot === GRAIL_SLOT && grailBearerIn(c, t) === null) {
    return { code: "TARGET_INVALID" };
  }
  // One Capitol per player: the hall chain's top step is barred everywhere else.
  if (isCapitol(b) && t.owner !== null && playerHasCapitol(c, t.owner, t.id)) {
    return { code: "ALREADY_BUILT", building: id };
  }
  const miss = missing(c, t, b.cost);
  if (miss !== null) return miss;
  return null;
}

export const isCapitol = (b: BuildingDef): boolean =>
  b.slot === CAPITOL_SLOT &&
  b.modifiers.some((m) => m.stat === "income.gold" && m.value >= CAPITOL_INCOME);

/**
 * Section 12 - one Capitol per player, on the way in as well as on the way up.
 *
 * `buildIllegality` already refuses a second Capitol at the drawing board; the
 * other half of the rule is capture. The classic demotes the *captured* town's Capitol
 * to the step below - the conqueror keeps his own - so taking a rival's capital
 * is worth a town and not a second four thousand a day.
 *
 * Returns the building that was demoted, or null when nothing was.
 */
export function demoteCapitol(c: Ctx, id: TownId, owner: PlayerId): BuildingDefId | null {
  const t = c.s.towns[id];
  if (t === undefined) return null;
  let capitol: BuildingDef | null = null;
  for (const b of builtDefs(c, t)) if (isCapitol(b)) capitol = b;
  if (capitol === null) return null;
  if (!playerHasCapitol(c, owner, id)) return null;
  const below = capitol.upgradeOf;
  const built = t.built.filter((x) => x !== capitol.id);
  // A Capitol with nothing under it would leave the town hall-less, so the
  // demotion is a no-op rather than a demolition.
  if (below === null) return null;
  if (!built.includes(below)) built.push(below);
  built.sort();
  patchTown(c, id, { built });
  return capitol.id;
}

function playerHasCapitol(c: Ctx, owner: PlayerId, except: TownId): boolean {
  for (const id of entityKeys(c.s.towns)) {
    if (id === except) continue;
    const t = c.s.towns[id]!;
    if (t.owner !== owner) continue;
    for (const b of builtDefs(c, t)) if (isCapitol(b)) return true;
  }
  return false;
}

function missing(c: Ctx, t: TownState, cost: Partial<Resources>): Reason | null {
  if (t.owner === null) return null;
  const p = c.s.players[t.owner];
  if (p === undefined) return null;
  for (const r of Object.keys(cost).sort() as ResourceKind[]) {
    const need = cost[r] ?? 0;
    if (need > p.resources[r]) {
      return { code: "INSUFFICIENT_RESOURCES", resource: r, have: p.resources[r], need };
    }
  }
  return null;
}

/**
 * Record a finished building. An upgrade replaces the step it upgrades in the
 * built list, so the town screen keeps one entry per slot and `built` never
 * grows a stale Village Hall next to a Capitol.
 */
export function addBuilding(c: Ctx, id: TownId, b: BuildingDef): void {
  const t = c.s.towns[id];
  if (t === undefined) return;
  const built = t.built.filter((x) => x !== b.upgradeOf);
  built.push(b.id);
  built.sort();
  patchTown(c, id, { built, builtThisTurn: true });
  c.events.push({ type: "BuildingBuilt", town: id, building: b.id });
}

// ------------------------------------------------------------- mage guild

/**
 * Fill every guild level the town can offer.
 *
 * Spells are drawn from `town.growth` - the town's own stream - so that adding
 * a roll to a hero or a battle never reshuffles a guild that was already
 * generated in a saved game (D5). Levels already filled are left alone: the classic
 * fixes a guild's contents the first time it is built.
 */
export function fillGuild(c: Ctx, id: TownId): void {
  const t = c.s.towns[id];
  if (t === undefined) return;
  const level = guildLevelOf(c, t);
  if (level === 0) return;
  const faction = c.content.factions.get(t.faction);
  const bias = faction?.guildBias ?? null;
  const guild: SpellDefId[][] = [];
  for (let i = 0; i < level; i++) guild.push([...(t.guildSpells[i] ?? [])]);

  // Section 12 - the Library. A building that widens the guild is authored as
  // a `guildSpells` modifier, so the rule is "every level holds this many more"
  // and no code knows the building's name.
  const extra = resolveStat(0, townModifiers(c, t).filter((m) => m.stat === "guildSpells")).value;

  for (let lvl = 1; lvl <= level; lvl++) {
    const want = (GUILD_SPELL_COUNT[lvl - 1] ?? 1) + (extra > 0 ? extra : 0);
    const have = guild[lvl - 1]!;
    if (have.length >= want) continue;
    const pool = spellPool(c.content, lvl, have);
    while (have.length < want && pool.length > 0) {
      const i = pickSpell(c, pool, bias);
      have.push(pool[i]!);
      pool.splice(i, 1);
    }
    have.sort();
    c.events.push({ type: "GuildSpellsSet", town: id, level: lvl, spells: [...have] });
  }
  patchTown(c, id, { guildSpells: guild });
}

/**
 * The spells of one level a guild may draw.
 *
 * Adventure spells are in the pool. The classic's guilds are where Homeward Gate and
 * Blink Step are found, and excluding them would leave the shrines as the
 * only source of half the spell list.
 */
function spellPool(
  content: ContentRegistry, level: number, exclude: readonly SpellDefId[],
): SpellDefId[] {
  const out: SpellDefId[] = [];
  for (const id of [...content.spells.keys()].sort()) {
    const s = content.spells.get(id)!;
    if (s.level !== level || !(s.combat || s.adventure)) continue;
    // Carried, never taught: no guild holds an `artifactOnly` spell.
    if (s.artifactOnly === true) continue;
    if (exclude.includes(id)) continue;
    out.push(id);
  }
  return out;
}

function pickSpell(
  c: Ctx, pool: readonly SpellDefId[], bias: Readonly<Record<SpellSchool, number>> | null,
): number {
  if (bias === null) return rollBounded(c, "town.growth", pool.length);
  const weights = pool.map((id) => {
    const s = c.content.spells.get(id)!;
    const w = bias[s.school] ?? 0;
    return w > 0 ? w : 1;
  });
  return rollWeighted(c, "town.growth", weights);
}

/**
 * Every spell in this town's guild that this hero could take, in book order.
 *
 * Reads `guildSpells` and never rolls: the guild's contents are fixed the first
 * time it is built (`fillGuild`), and re-rolling them per visit would make the
 * spells a hero ends up with depend on how many times they walked in - a
 * determinism bug and a balance one at the same time.
 */
export function learnableGuildSpells(
  c: Ctx, t: TownState, h: HeroState,
): readonly SpellDefId[] {
  const out: SpellDefId[] = [];
  const level = guildLevelOf(c, t);
  for (let lvl = 1; lvl <= level; lvl++) {
    for (const spell of t.guildSpells[lvl - 1] ?? []) {
      if (learnIllegality(c, h, spell) !== null) continue;
      out.push(spell);
    }
  }
  out.sort();
  return out;
}

/**
 * A hero standing in a town copies its guild into their book, as far as Wisdom
 * lets them. Called from every way into a town - walking in, being hired,
 * clicking in, arriving by Homeward Gate - because the classic teaches on arrival, not
 * on a separate action.
 */
/**
 * Everything that happens because a hero is now standing in this town: its
 * guild teaches him, and its special buildings bless him (Section 12). One
 * function, because there are five doors into a town - walking in, being
 * hired, clicking in from next door, capturing it, and Homeward Gate - and a
 * blessing that only some of them grant is a bug nobody would find.
 */
export function arriveInTown(c: Ctx, heroId: HeroId, id: TownId): void {
  const t = c.s.towns[id];
  const h = c.s.heroes[heroId];
  if (t !== undefined && h !== undefined) applyTownVisit(c, h, t);
  learnGuildSpells(c, heroId, id);
}

export function learnGuildSpells(c: Ctx, heroId: HeroId, id: TownId): number {
  const t = c.s.towns[id];
  const h = c.s.heroes[heroId];
  if (t === undefined || h === undefined) return 0;
  let learned = 0;
  // `learnSpell` re-reads the hero, so a book that grew on the previous
  // iteration is the book the next grant is checked against.
  for (const spell of learnableGuildSpells(c, t, h)) {
    if (learnSpell(c, heroId, spell)) learned++;
  }
  return learned;
}

// ------------------------------------------------------------- recruitment

/** The dwelling pool a creature draws from: its own, or its base form's. */
export function poolKeyFor(content: ContentRegistry, def: CreatureDefId): string {
  const d = content.creatures.get(def);
  if (d === undefined) return def as string;
  return (d.upgradeOf ?? d.id) as string;
}

export function availableOf(c: Ctx, t: TownState, def: CreatureDefId): number {
  const direct = t.available[def as string] ?? 0;
  const key = poolKeyFor(c.content, def);
  if (key === (def as string)) return direct;
  return direct + (t.available[key] ?? 0);
}

/** Spend `count` from the pools that back `def`, own pool first. */
export function spendAvailable(
  c: Ctx, id: TownId, def: CreatureDefId, count: number,
): void {
  const t = c.s.towns[id];
  if (t === undefined) return;
  const available: Record<string, number> = { ...t.available };
  let left = count;
  const own = def as string;
  const take = (k: string): void => {
    const have = available[k] ?? 0;
    const n = have < left ? have : left;
    if (n <= 0) return;
    available[k] = have - n;
    left -= n;
  };
  take(own);
  const base = poolKeyFor(c.content, def);
  if (base !== own) take(base);
  patchTown(c, id, { available });
}

export function recruitCost(
  content: ContentRegistry, def: CreatureDefId, count: number,
): Partial<Resources> {
  const d = content.creatures.get(def);
  if (d === undefined) return {};
  const out: Record<string, number> = {};
  for (const k of Object.keys(d.cost).sort()) {
    const v = (d.cost as Record<string, number>)[k] ?? 0;
    if (v !== 0) out[k] = v * count;
  }
  return out as Partial<Resources>;
}

/** The creature forms a town may recruit: each built dwelling's creature. */
export function recruitable(c: Ctx, t: TownState): readonly CreatureDefId[] {
  return dwellingCreatures(c, t);
}

// ----------------------------------------------------------------- tavern

export const TAVERN_OFFERS = 2;

/**
 * Refill a player's tavern. Own-faction heroes are drawn first, as in the classic,
 * and heroes already on the map or already offered are never offered twice.
 */
export function refreshTavern(c: Ctx, p: PlayerId): void {
  const pl = c.s.players[p];
  if (pl === undefined) return;
  const taken = new Set<string>();
  for (const id of entityKeys(c.s.heroes)) taken.add(c.s.heroes[id]!.def as string);
  for (const id of entityKeys(c.s.players)) {
    if (id === p) continue;
    for (const d of c.s.players[id]!.tavern) taken.add(d as string);
  }

  const own: string[] = [];
  const other: string[] = [];
  for (const id of [...c.content.heroes.keys()].sort()) {
    if (taken.has(id as string)) continue;
    const hd = c.content.heroes.get(id)!;
    ((hd.faction as string) === (pl.faction as string) ? own : other).push(id as string);
  }

  const offers: string[] = [];
  const draw = (pool: string[]): void => {
    if (pool.length === 0 || offers.length >= TAVERN_OFFERS) return;
    const i = rollBounded(c, "hero.offer", pool.length);
    offers.push(pool[i]!);
    pool.splice(i, 1);
  };
  draw(own);
  draw(own.length > 0 ? own : other);
  while (offers.length < TAVERN_OFFERS && (own.length > 0 || other.length > 0)) {
    draw(own.length > 0 ? own : other);
  }
  patchPlayer(c, p, { tavern: offers.sort().map(asHeroDefId) });
}

// ---------------------------------------------------------------- upgrades

/**
 * The stacks in `armyId` that this town can upgrade, and what each would cost.
 *
 * A town upgrades only into creatures its own dwellings produce: building the
 * upgraded dwelling is what unlocks it, which is the whole point of paying for
 * the upgrade building. That is the difference between here and a Warcraft Keep,
 * which takes anything - so the gate is `dwellingCreatures`, the same list
 * recruitment is drawn from, rather than a second notion of what a town offers.
 *
 * Full price: the classic's Warcraft Keep discount exists precisely because a town does
 * not discount. See `upgrade.ts` for the difference rule the price comes from.
 *
 * Note this reads the dwellings, not the creature pool. Upgrading a stack you
 * already own does not consume a week's growth - it is not a purchase of new
 * creatures - so a town with an empty dwelling still upgrades.
 */
export function townUpgradeOffers(
  c: Ctx, t: TownState, armyId: ArmyId,
): readonly UpgradeOffer[] {
  const army = c.s.armies[armyId];
  if (army === undefined) return [];
  const offered = new Set<string>(dwellingCreatures(c, t).map((id) => id as string));
  return upgradeOffers(
    c.content, army.slots,
    (to) => offered.has(to.id as string),
    () => FULL_PRICE_BP,
  );
}

/**
 * The armies a town may upgrade: its garrison, and any hero standing in it.
 *
 * Both, rather than `recruitTargetArmy`'s single answer, because the town
 * screen shows the garrison and the visiting hero side by side and a player
 * expects to upgrade either. Recruitment has to pick one because new creatures
 * go somewhere; an upgrade names the stack it acts on, so it does not.
 */
export function upgradableArmies(c: Ctx, t: TownState): readonly ArmyId[] {
  const out: ArmyId[] = [t.garrison];
  for (const h of [t.visitingHero, t.garrisonHero]) {
    if (h === null) continue;
    const hero = c.s.heroes[h];
    if (hero !== undefined && !out.includes(hero.army)) out.push(hero.army);
  }
  return out;
}
